import React, { useState } from "react";
import { ChevronDown, ChevronRight, HelpCircle } from "lucide-react";

export type AiFieldSource = "extracted" | "inferred" | "fallback" | "missing";

export interface AiFieldSourceEntry {
  source: AiFieldSource;
  raw?: string;
  alternatives?: string[];
  fallbackKey?: string;
  note?: string;
}

export const AI_FIELD_LABELS: Record<string, string> = {
  specialty: "Specialty",
  minimum_skill_level: "Skill level",
  start_date: "Start date",
  end_date: "End date",
  start_time: "Start time",
  end_time: "End time",
  rate_per_day: "Daily rate",
  rate_per_hour: "Hourly rate",
  supportLevel: "Support",
  description: "Description",
  travel: "Travel",
  accommodation: "Accommodation",
  hospitalName: "Hospital",
};

const SOURCE_META: Record<
  AiFieldSource,
  { label: string; className: string; title: string }
> = {
  extracted: {
    label: "From email",
    className: "bg-emerald-50 text-emerald-800 border-emerald-200",
    title: "Copied directly from the email or attachment",
  },
  inferred: {
    label: "Mapped",
    className: "bg-amber-50 text-amber-800 border-amber-200",
    title:
      "We interpreted email wording and matched it to your hospital menu — please confirm",
  },
  fallback: {
    label: "Default",
    className: "bg-orange-50 text-orange-800 border-orange-200",
    title: "Filled from your AI Shifts default settings, not from the email",
  },
  missing: {
    label: "Missing",
    className: "bg-rose-50 text-rose-800 border-rose-200",
    title: "Not found in the email — you may need to enter it manually",
  },
};

interface AiShiftProvenancePanelProps {
  fieldSources?: Record<string, AiFieldSourceEntry> | null;
  specialtyAlternatives?: string[];
  skillLevelAlternatives?: string[];
  confidencePercentage?: number | null;
  defaultExpanded?: boolean;
  /** Keeps expanded review from crushing the validate modal body. */
  compact?: boolean;
  className?: string;
}

const AiShiftProvenancePanel: React.FC<AiShiftProvenancePanelProps> = ({
  fieldSources,
  specialtyAlternatives = [],
  skillLevelAlternatives = [],
  confidencePercentage,
  defaultExpanded = false,
  compact = false,
  className = "",
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showAllFields, setShowAllFields] = useState(false);

  const entries = Object.entries(fieldSources || {}).filter(
    ([key, entry]) => entry && AI_FIELD_LABELS[key],
  );

  const reviewEntries = entries.filter(
    ([, e]) =>
      e.source === "inferred" ||
      e.source === "fallback" ||
      e.source === "missing",
  );
  const needsReview = reviewEntries.length;
  const visibleEntries =
    compact && !showAllFields && needsReview > 0 ? reviewEntries : entries;

  if (!entries.length) {
    return null;
  }

  const confidenceLabel =
    typeof confidencePercentage === "number" && Number.isFinite(confidencePercentage)
      ? `${Math.round(confidencePercentage)}% model score`
      : null;

  return (
    <div
      className={`rounded-lg border border-slate-200 bg-white ${className}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-slate-50 rounded-lg transition-colors"
      >
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {expanded ? (
            <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
          )}
          <span className="text-xs font-semibold text-slate-700">
            Field review
          </span>
          {needsReview > 0 ? (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
              {needsReview} to verify
            </span>
          ) : (
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-medium">
              Looks complete
            </span>
          )}
          {confidenceLabel && (
            <span className="text-[11px] text-slate-500">{confidenceLabel}</span>
          )}
        </div>
        <span
          title="Mapped = we matched email text to your menu. Default = from AI settings. Missing = not in email."
          className="text-slate-400 hover:text-slate-600"
        >
          <HelpCircle className="w-4 h-4" />
        </span>
      </button>

      {expanded && (
        <div
          className={`px-3 pb-3 border-t border-slate-100 pt-2 space-y-2 ${
            compact ? "max-h-[140px] overflow-y-auto" : ""
          }`}
        >
          <p className="text-[11px] text-slate-500 leading-relaxed">
            <strong className="text-slate-600">Mapped</strong> means the email
            did not use your exact menu label — we matched wording. Confirm
            specialty/skill before publishing.
          </p>

          {(specialtyAlternatives.length > 0 ||
            skillLevelAlternatives.length > 0) && (
            <div className="text-xs text-slate-600 space-y-1 rounded-md bg-slate-50 p-2">
              {specialtyAlternatives.length > 0 && (
                <div>
                  <span className="font-semibold">Other specialties in email: </span>
                  {specialtyAlternatives.join(", ")}
                </div>
              )}
              {skillLevelAlternatives.length > 0 && (
                <div>
                  <span className="font-semibold">Other skill levels in email: </span>
                  {skillLevelAlternatives.join(", ")}
                </div>
              )}
            </div>
          )}

          <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {visibleEntries.map(([key, entry]) => {
              const meta = SOURCE_META[entry.source] || SOURCE_META.missing;
              return (
                <div
                  key={key}
                  className="flex items-center justify-between gap-2 rounded-md border border-slate-100 bg-slate-50/50 px-2.5 py-2 text-xs"
                >
                  <div className="min-w-0 truncate">
                    <span className="font-medium text-slate-700">
                      {AI_FIELD_LABELS[key]}
                    </span>
                    {entry.raw && entry.source !== "extracted" && (
                      <span
                        className="text-slate-400 ml-1 truncate"
                        title={entry.raw}
                      >
                        ({entry.raw})
                      </span>
                    )}
                  </div>
                  <span
                    title={meta.title}
                    className={`shrink-0 px-1.5 py-0.5 rounded border text-[10px] font-semibold ${meta.className}`}
                  >
                    {meta.label}
                  </span>
                </div>
              );
            })}
          </div>

          {compact && needsReview > 0 && needsReview < entries.length && (
            <button
              type="button"
              onClick={() => setShowAllFields((v) => !v)}
              className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900"
            >
              {showAllFields
                ? "Show only fields to verify"
                : `Show all ${entries.length} fields`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AiShiftProvenancePanel;
